import { useLazyQuery } from '@apollo/react-hooks'
import MaxAlert from '@berlitz/alert'
import Spacer from '@berlitz/spacer'
import MyBerlitzLessons from '@classes/Lessons'
import PortalStudentProfile from '@classes/StudentProfile'
import PageHeader from '@components/PageHeader'
import Select from '@components/Select'
import { InstructorSearchResultInfo, InstructorsQueryResult } from '@interfaces/Instructor'
import { ISearchInstructorFilters } from '@interfaces/ISearchInstructorFilters'
import { SelectorContainer } from '@layouts/LearningPath/Layout/style'
import { GET_INSTRUCTORS_BY_PROGRAM_ID } from '@queries/getInsructorsByProgramId'
import { updateDashboardActiveProgram } from '@redux/actions/dashboard'
import { getStoredMaterials } from '@utils/helpers/material'
import { getStorageAdapter, PORTAL_KEYS } from '@shell/adapters/storage'
import { SelectValue } from 'antd/lib/select'
import { debounce, isEmpty, uniq } from 'lodash'
import moment from 'moment-timezone'
import Router from 'next/router'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Skeleton from 'react-loading-skeleton'
import { useDispatch } from 'react-redux'
import { BlockedProvider, FaveProvider } from './context'
import { ScheduleInstructorsProps } from './interface'
import ManageInstructorView from './ManageInstructorView'
import PageFilters from './PageFilters'
import SearchResults from './SearchResults'
import { AlertWrapper, ForbiddenBox } from './style'
import { Col, Row } from 'react-styled-flexboxgrid'
import AtRiskNotificationCard from '@components/AtRiskNotificationCard'
import { FMIStudentProfileContext } from './context'
import { PROGRAM_TYPES, SCHEDULE_STATUS } from '@utils/constants'
import ImageLocked from '@berlitzplatforms/micro.image.locked'
import BulkForm, { BulkFormLoader } from './BulkForm'
import { usePortalFeatures } from '@components/RoleBasedView'
import { FMIBulkFormProvider } from './BulkForm/Form/useFMIBulkForm'
import { FMIBulkSelectInstructorsProvider } from './BulkForm/SelectInstructors/useSelectInstructors'
import ProfileModal from './BulkForm/SelectInstructors/ProfileModal'
import PortalInstructorProfile from '@classes/InstructorProfile'
import InvalidProgramStatus from '@layouts/Scheduling/ScheduleOptions/InvalidProgramStatus'
import { useRouter } from 'next/router'

const ScheduleInstructors: React.FC<ScheduleInstructorsProps> = ({
  loading: pageLoading,
  sessions = new MyBerlitzLessons(),
}) => {
  const dispatch = useDispatch()
  const { state } = usePortalFeatures({
    configId: 'flag::FMI-bulksearch',
  })
  const {
    userProfile: profile,
    activeMaterial,
    baseRoute,
    getRouteParams,
    isAdmin,
    isBulkPage,
  } = useContext(FMIStudentProfileContext)
  const router = useRouter()
  const studentProfile = new PortalStudentProfile(profile?.StudentProfile?.StudentProfileInfo)
  const portalPrograms = studentProfile.RegistrationPortalPrograms
  const stored = getStoredMaterials()?.[profile.SFId]
  const programsDropdown = portalPrograms
    .studentProgramsSortedDropdown(() => moment.tz(moment(), profile.Timezone))
    .filter(({ data }) => data?.Status !== SCHEDULE_STATUS.EXPIRED)
    .filter(({ data }) => ![...PROGRAM_TYPES.ON_DEMAND_1, ...PROGRAM_TYPES.FLEX_BOD, ...PROGRAM_TYPES.F2F_STAR].includes(data?.ProgramType ?? ''))
  const initialProgram = portalPrograms.getProgram(stored || activeMaterial || programsDropdown[0]?.value, true)
  const [currProgramId, setCurrentProgramId] = useState(
    (Router?.query?.programId as string) ||
      (programsDropdown.length === 1
        ? programsDropdown[0]?.value
        : programsDropdown.some(
              ({ data }) =>
                data?.ProgramId === initialProgram.ProgramId ||
                data.Materials?.some(({ LPID }) => LPID === activeMaterial)
            )
          ? initialProgram.ProgramId || activeMaterial
          : programsDropdown[0]?.value)
  )
  const currProgram = portalPrograms.getProgram(currProgramId as string)
  const isBulkOnlyFeature =
    (state.allowed && currProgram?.hasBulkOnlyFeature() && !Router.query.sessionId) || isBulkPage

  const intl = useIntl()
  const [instructors, setInstructors] = useState<InstructorSearchResultInfo[]>()
  const [pastInstructors, setPastInstructors] = useState<InstructorSearchResultInfo[]>()
  const [faveInstructors, setFaveInstructors] = useState<InstructorSearchResultInfo[]>()
  const [blockedInstructors, setBlockedInstructors] = useState<InstructorSearchResultInfo[]>()
  const [filters, setFilters] = useState({})
  const [filterName, setFilterName] = useState('')
  const [pristine, setPristine] = useState(true)
  const [loading, setLoading] = useState(true)
  const [searchLoading, setSearchLoading] = useState(false)
  const [lang, setLang] = useState('English')
  const [programId, setProgramId] = useState('')
  const [selectedProfile, setSelectedProfile] = useState<PortalInstructorProfile | null>(null)
  const [openProfileModal, setOpenProfileModal] = useState(false)
  const [getInstructors, { error, loading: fetching, data }] = useLazyQuery<InstructorsQueryResult>(
    GET_INSTRUCTORS_BY_PROGRAM_ID,
    { fetchPolicy: 'no-cache' }
  )
  const readOnly = currProgram.isReadOnly(!isEmpty(Router.query.sessionId))

  const updateFilters = useCallback(
    (type: string, value: string[] | string | undefined) => {
      const newFilter = { ...filters }
      newFilter[type] = typeof value === 'string' ? value : uniq(value)
      setFilters(newFilter)
      setPristine(false)
    },
    [filters]
  )

  const bulkUpdateFilters = useCallback(
    (newFilters: ISearchInstructorFilters) => {
      setFilters(newFilters)
      setPristine(false)
    },
    [filters]
  )

  const handleDDChange = useMemo(() => {
    const debounced = debounce((value: SelectValue) => {
      updateFilters('name', value.toString())
      setSearchLoading(false)
    }, 200)
    return (value: SelectValue) => {
      setFilterName(value.toString())
      setSearchLoading(true)
      return debounced(value)
    }
  }, [filters])

  const handleChange = useMemo(() => {
    const debounced = debounce((evt: React.ChangeEvent<HTMLInputElement>) => {
      updateFilters('name', evt.target.value)
      setSearchLoading(false)
    }, 250)
    return (evt: React.ChangeEvent<HTMLInputElement>) => {
      setFilterName(evt.target.value)
      evt.persist()
      setSearchLoading(true)
      return debounced(evt)
    }
  }, [filters])

  const fetchData = () => {
    if (!currProgramId) {
      return
    }

    setProgramId(currProgramId)
    setLang(currProgram?.program?.Language || 'English')
    setPristine(false)
    getInstructors({
      variables: {
        query: {
          StudentProfileId: profile.SFId,
          ProgramId: currProgramId,
        },
      },
    })

    setFilterName('')
    setFilters({})
    setPristine(true)
    setPastInstructors(undefined)
    setFaveInstructors(undefined)
    setBlockedInstructors(undefined)
  }

  const goToInstructor = (tab: string, instructorId?: string) => {
    if (isBulkOnlyFeature) {
      setSelectedProfile(
        new PortalInstructorProfile(
          (instructors || [])?.find((instructor) => instructor?.InstructorId === instructorId)
        )
      )
      setOpenProfileModal(true)
      return
    }
    Router.push(
      `${baseRoute}/${tab}${isBulkPage ? '/bulk' : ''}?instructorId=${instructorId}&programId=${programId}&${getRouteParams(
        ['instructorId', 'programId']
      )}`
    )
  }

  useEffect(() => {
    if (fetching) {
      setInstructors(undefined)
    }
  }, [fetching])

  useEffect(() => {
    if (error) {
      setInstructors(undefined)
    }
  }, [error])

  useEffect(() => {
    if (data && data.getInstructorsByProgramId) {
      const mappedData = data.getInstructorsByProgramId.map((item) => item.instructorInfo)
      const cleanData = mappedData.filter((item) => !item.IsBlocked && !item.Name?.match(/^#Test/))
      const orderedPast = sessions.getOrderedPastInstructors(cleanData?.filter((instructor) => instructor.IsPrevious))

      setPastInstructors(orderedPast)
      setFaveInstructors(cleanData?.filter((instructor) => instructor.IsPreferred))
      setBlockedInstructors(mappedData.filter((item) => item.IsBlocked && !item.Name?.match(/^#Test/)))
      setInstructors(cleanData)
    }
  }, [data, JSON.stringify(sessions.validSessions())])

  useEffect(() => {
    if (currProgramId && programsDropdown.length) {
      fetchData()
    }
  }, [currProgramId])

  useEffect(() => {
    setLoading(pageLoading || fetching)
  }, [pageLoading, fetching])

  if (!pageLoading && !programsDropdown.length && isAdmin) {
    return (
      <ForbiddenBox>
        <Spacer size="xxxl" />
        <FormattedMessage
          id="This student does not have access to Follow My Instructor"
          defaultMessage="This student does not have access to Follow My Instructor"
        />
        <Spacer size="lg" />
        <ImageLocked />
        <Spacer size="xxxl" />
      </ForbiddenBox>
    )
  }

  if (PROGRAM_TYPES.ON_DEMAND.includes(currProgram.ProgramTypeDisplay ?? '')) {
    return (
      <>
        <PageHeader
          title="Instructors"
          noHR={true}
          loading={pageLoading}
          timezone={profile.Timezone}
          isAdmin={isAdmin}
        />
        <Spacer size="xxs" />

        {(pageLoading || programsDropdown.length > 1) && (
          <SelectorContainer xs={12} md={12} lg={studentProfile.AtRiskProgram ? 6 : 12}>
            {pageLoading ? (
              <div>
                <Skeleton width="80px" height="14px" />
                <Spacer size="xs" />
                <Skeleton width="100%" height="35px" />
              </div>
            ) : (
              programsDropdown.length > 1 && (
                <>
                  <Select
                    label={intl.formatMessage({ id: 'Course' })}
                    name="Program"
                    value={currProgramId || ''}
                    onChange={(program) => {
                      const materials = getStoredMaterials()
                      const material = portalPrograms.getProgram(program)?.program?.Materials?.[0]?.LPID
                      if (material) {
                        materials[profile.SFId] = portalPrograms.getProgram(program)?.program?.Materials?.[0]?.LPID
                        getStorageAdapter().set(PORTAL_KEYS.SELECTED_MATERIAL, JSON.stringify(materials))
                      }

                      setCurrentProgramId(program)
                      dispatch(updateDashboardActiveProgram(program))
                    }}
                    options={programsDropdown}
                    disableMargin
                    responsive
                  />
                  {studentProfile.AtRiskProgram && <Spacer size="md" />}
                </>
              )
            )}
          </SelectorContainer>
        )}

        {!pageLoading && (
          <InvalidProgramStatus
            {...currProgram.program}
            customTitle={intl.formatMessage({
              id: 'You do not need to book any instructors for this course.',
              defaultMessage: 'You do not need to book any instructors for this course.',
            })}
            customBody={intl.formatMessage({
              id: 'Please contact your learning center for more information, or raise a case using the button below.',
              defaultMessage:
                'Please contact your learning center for more information, or raise a case using the button below.',
            })}
          />
        )}
      </>
    )
  }

  return (
    <FaveProvider
      studentId={profile.SFId}
      programId={programId}
      faveInstructors={faveInstructors || []}
      rawInstructors={instructors}
    >
      <BlockedProvider
        studentId={profile.SFId}
        programId={programId}
        blockedInstructors={blockedInstructors || []}
        rawInstructors={instructors}
      >
        <PageHeader
          title="Instructors"
          noHR={true}
          loading={pageLoading}
          timezone={profile.Timezone}
          isAdmin={isAdmin}
        />
        <Spacer size="xxs" />
        <Row>
          {(pageLoading || programsDropdown.length > 1) && (
            <SelectorContainer xs={12} md={12} lg={studentProfile.AtRiskProgram ? 6 : 12}>
              {pageLoading ? (
                <div>
                  <Skeleton width="80px" height="14px" />
                  <Spacer size="xs" />
                  <Skeleton width="100%" height="35px" />
                </div>
              ) : (
                programsDropdown.length > 1 && (
                  <>
                    <Select
                      label={intl.formatMessage({ id: 'Course' })}
                      name="Program"
                      value={currProgramId || ''}
                      onChange={(program) => {
                        const materials = getStoredMaterials()
                        const material = portalPrograms.getProgram(program)?.program?.Materials?.[0]?.LPID
                        if (material) {
                          materials[profile.SFId] = portalPrograms.getProgram(program)?.program?.Materials?.[0]?.LPID
                          getStorageAdapter().set(PORTAL_KEYS.SELECTED_MATERIAL, JSON.stringify(materials))
                        }

                        setCurrentProgramId(program)
                        dispatch(updateDashboardActiveProgram(program))
                      }}
                      options={programsDropdown}
                      disableMargin
                      responsive
                      disabled={router.query.sessionId ? true : false}
                    />
                    {studentProfile.AtRiskProgram && <Spacer size="md" />}
                  </>
                )
              )}
            </SelectorContainer>
          )}
          {studentProfile.AtRiskProgram && (
            <Col xs={12} md={12} lg={6}>
              {!pageLoading && <AtRiskNotificationCard noButton={studentProfile.AtRiskProgram?.hasBulkOnlyFeature()} />}
            </Col>
          )}
        </Row>
        {!readOnly && !loading && currProgram.isGroupLead && currProgram.isStudentAbleToBook ? (
          <AlertWrapper>
            <MaxAlert type="warning">
              <FormattedMessage
                id="Don't forget. You are the lead student for your group. You are booking lessons for all attendees."
                defaultMessage="Don't forget. You are the lead student for your group. You are booking lessons for all attendees."
              />
            </MaxAlert>
          </AlertWrapper>
        ) : null}
        {readOnly &&
          !loading &&
          (!currProgram.isStudentAbleToBook || currProgram.IsOrientationRequired || currProgram.IsLessonsBookedOut) && (
            <AlertWrapper>
              <MaxAlert type="warning">
                {!currProgram.isStudentAbleToBook ? (
                  <FormattedMessage
                    id={`You are in read-only mode. Your lessons are scheduled by ${
                      currProgram.program?.IsPrivate ? `the lead student.` : `your learning centre.`
                    }`}
                    defaultMessage={`You are in read-only mode. Your lessons are scheduled by ${
                      currProgram.program?.IsPrivate ? `the lead student.` : `your learning centre.`
                    }`}
                  />
                ) : currProgram.IsOrientationRequired ? (
                  <FormattedMessage
                    id="You are in read-only mode. Before we book your lessons let's organise your Getting Started or Orientation Session. Once this has been booked you can come back and book your lessons."
                    defaultMessage="You are in read-only mode. Before we book your lessons let's organise your Getting Started or Orientation Session. Once this has been booked you can come back and book your lessons."
                  />
                ) : currProgram.IsLessonsBookedOut ? (
                  <FormattedMessage
                    id="You are in read-only mode. All your lessons have been booked."
                    defaultMessage="You are in read-only mode. All your lessons have been booked."
                  />
                ) : null}
              </MaxAlert>
            </AlertWrapper>
          )}
        <Spacer size="lg" />
        <ManageInstructorView
          pastInstructors={pastInstructors}
          faveInstructors={faveInstructors}
          loading={loading}
          goToInstructor={goToInstructor}
          programId={currProgramId || undefined}
          isBulkOnlyFeature={isBulkOnlyFeature}
        />
        {isBulkOnlyFeature ? (
          <FMIBulkSelectInstructorsProvider>
            <FMIBulkFormProvider Program={currProgram}>
              {loading ? <BulkFormLoader /> : <BulkForm />}
            </FMIBulkFormProvider>
          </FMIBulkSelectInstructorsProvider>
        ) : (
          <>
            <PageFilters
              loading={loading}
              updateFilters={updateFilters}
              bulkUpdateFilters={bulkUpdateFilters}
              programLang={lang}
              handleInputChange={handleChange}
              handleDDChange={handleDDChange}
              inputValue={filterName}
              filters={filters}
              readOnly={readOnly}
              programId={programId}
            />
            <SearchResults
              instructors={instructors}
              loading={loading || searchLoading}
              filters={filters}
              pristine={pristine}
              setPristine={setPristine}
              clearSort={fetching}
              readOnly={readOnly}
              programId={programId}
              goToInstructor={goToInstructor}
            />
          </>
        )}
        {isBulkOnlyFeature ? (
          <ProfileModal open={openProfileModal} onClose={() => setOpenProfileModal(false)} Profile={selectedProfile} />
        ) : null}
      </BlockedProvider>
    </FaveProvider>
  )
}

export default ScheduleInstructors
